import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import type { PlanManagementTaskDto } from '../api/dto/plan-management-task.dto'
import { PlanManagementTaskStatusEnum } from '../api/enums/plan-management-task-status.enum'
import { getPlanManagementTasks } from '../api/plan-management-tasks-api'

export type { PlanManagementTaskDto }

export const DEFAULT_PAGE_SIZE = 50

export type SortField = 'createdAt' | 'status'

export type SortOrder = 'ASC' | 'DESC'

const PENDING_POLL_INTERVAL_MS = 5000

const PENDING_STATUSES = new Set([
  PlanManagementTaskStatusEnum.PENDING_FETCH,
  PlanManagementTaskStatusEnum.PENDING_COMPARE,
  PlanManagementTaskStatusEnum.MIGRATING,
])

const FETCH_BLOCKING_STATUSES = new Set([
  PlanManagementTaskStatusEnum.PENDING_FETCH,
])

export default function usePlanDataSnapshotList() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sort, setSort] = useState<SortField>('createdAt')
  const [order, setOrder] = useState<SortOrder>('DESC')

  const {
    data,
    isLoading: isInitialLoading,
    isPlaceholderData,
    error,
  } = useQuery({
    queryKey: ['planDataSnapshotList', page, pageSize, sort, order],
    queryFn: () =>
      getPlanManagementTasks({
        page,
        limit: pageSize,
        sort,
        order,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const rows = query.state.data?.rows ?? []
      return rows.some((r) => PENDING_STATUSES.has(r.status))
        ? PENDING_POLL_INTERVAL_MS
        : false
    },
  })

  const sortedData = useMemo(() => {
    const rows = data?.rows ?? []
    const pending = rows.filter((r) => PENDING_STATUSES.has(r.status))
    const rest = rows.filter((r) => !PENDING_STATUSES.has(r.status))
    return [...pending, ...rest]
  }, [data])

  const hasPendingTask = useMemo(
    () => sortedData.some((row) => FETCH_BLOCKING_STATUSES.has(row.status)),
    [sortedData]
  )

  const handlePageChange = useCallback(
    (newPage: number, newPageSize?: number) => {
      setPage(newPage)
      if (newPageSize !== undefined && newPageSize !== pageSize) {
        setPageSize(newPageSize)
        setPage(1)
      }
    },
    [pageSize]
  )

  const handleSortChange = useCallback(
    (field: SortField, direction: SortOrder) => {
      setSort(field)
      setOrder(direction)
      setPage(1)
    },
    []
  )

  return {
    data: sortedData,
    total: data?.total ?? 0,
    isLoading: isInitialLoading || isPlaceholderData,
    error,
    page,
    pageSize,
    sort,
    order,
    hasPendingTask,
    handlePageChange,
    handleSortChange,
  }
}
